import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import SignatureLines from './SignatureLines';
import ClientBlock from './ClientBlock';
import printWhenReady from '../utils/printWhenReady';
import './InvoicePrintView.css';
import { formatDay, todayDR } from '../utils/dates';
import { useFileName } from '../utils/fileName';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const STATE = {
  active: 'Activo', nearly_used: 'Por agotarse', upcoming: 'Por empezar', used_up: 'Agotado', expired: 'Vencido', cancelled: 'Cancelado',
};

// An acuerdo's statement (Conduces → Acuerdos → Imprimir), the twin of a compromiso's (CommitmentPrintView.js): every conduce
// deducted from what the company owes the client, with the balance after each — for our records and for the client if a
// delivery is disputed. With ?detalle=1 each conduce carries its lines. Saved as PDF under the acuerdo's name.
const ClientDealPrintView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const detail = searchParams.get('detalle') === '1';
  const [d, setD] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/client-deals/${id}`).then(res => setD(res.data)).catch(err => setError(err.response?.data?.message || 'No se pudo cargar el acuerdo.'));
  }, [id]);

  useEffect(() => {
    if (!d) return undefined;
    let cancelPrint = () => {};
    const t = setTimeout(() => { cancelPrint = printWhenReady(); }, 350);
    return () => { clearTimeout(t); cancelPrint(); };
  }, [d]);

  // Saved as PDF under the acuerdo's own name, like a compromiso's.
  useFileName(d && d.title.replace(/[\\/:*?"<>|]+/g, ' ').trim());

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!d) return <div className="p-4 text-muted">Cargando acuerdo...</div>;

  const covered = d.entries.filter(e => e.covered > 0.005 || e.value <= 0.005);

  return (
    <div className="invoice-page-wrapper">
      <div className="no-print text-center mb-3">
        <button className="btn btn-primary" onClick={() => window.print()}>
          <i className="bi bi-printer" /> Imprimir / Guardar como PDF
        </button>
      </div>

      <div className="invoice-paper">
        <div className="invoice-body">
          <div className="invoice-header-row">
            <div className="invoice-company-block">
              <img src="/CGCLogo.png" alt="Caribbean Gas Company" className="invoice-logo" />
              <div style={{ fontWeight: 700, color: '#0E1A24' }}>{COMPANY_INFO.name}</div>
              <div>RNC: {COMPANY_INFO.rnc}</div>
              <div>{COMPANY_INFO.address}</div>
              <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            </div>
            <div className="invoice-meta-block">
              <div className="invoice-title">ESTADO DE ACUERDO</div>
              <div><strong>{d.title}</strong></div>
              <div>Emitido: {formatDay(todayDR())}</div>
              <div>Vigencia: {formatDay(d.startDate)} – {d.endDate ? formatDay(d.endDate) : 'sin fecha de fin'}</div>
              <div>Estado: {STATE[d.state] || d.state}</div>
            </div>
          </div>

          <ClientBlock label="Cliente" client={d.client || {}} address={d.client?.address} />

          <table className="invoice-table">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th>Conduce</th>
                <th>Fecha</th>
                {detail && <th>Productos</th>}
                <th className="text-end">Valor</th>
                <th className="text-end">Descontado</th>
                <th className="text-end">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {covered.map((e, i) => (
                <tr key={`${e.conduce}-${i}`}>
                  <td className="text-center">{i + 1}</td>
                  <td>{e.number}{e.status === 'pending' ? ' (por entregar)' : ''}</td>
                  <td>{formatDay(e.day)}</td>
                  {detail && <td>{(e.items || []).map((it, j) => <div key={j}>{it.quantity} × {it.description}</div>)}</td>}
                  <td className="text-end">{money(e.value)}</td>
                  <td className="text-end">{money(e.covered)}{e.covered < e.value - 0.005 ? ' *' : ''}</td>
                  <td className="text-end">{money(e.balanceAfter)}</td>
                </tr>
              ))}
              {covered.length === 0 && <tr><td colSpan={detail ? 7 : 6} className="text-center">Todavía no hay conduces descontados.</td></tr>}
            </tbody>
          </table>

          <div className="invoice-totals">
            <div className="invoice-totals-box">
              <div className="invoice-totals-row"><span>Monto del acuerdo</span><span>{money(d.amount)}</span></div>
              <div className="invoice-totals-row"><span>Usado ({covered.length} conduce{covered.length === 1 ? '' : 's'})</span><span>{money(d.used)}</span></div>
              <div className="invoice-totals-row grand"><span>Saldo</span><span>{money(d.balance)}</span></div>
            </div>
          </div>

          {d.notes && (
            <div className="invoice-bill-to">
              <div className="label">Notas</div>
              <div style={{ whiteSpace: 'pre-line' }}>{d.notes}</div>
            </div>
          )}
          {d.cancelledAt && (
            <div className="invoice-bill-to">
              <div className="label">Cancelado</div>
              <div>{formatDay(d.cancelledAt)}: {d.cancelReason}</div>
            </div>
          )}

          <div className="invoice-disclaimer">
            Relación de los conduces descontados de este acuerdo según los registros de {COMPANY_INFO.name} al {formatDay(todayDR())}.
            Montos sin ITBIS, al precio del cliente (o el de lista) de cada conduce.
            {covered.some(e => e.covered < e.value - 0.005) && ' * Conduce cubierto en parte: el resto se factura aparte.'}
          </div>
        </div>
        <div className="invoice-bottom">
          <SignatureLines clientName={d.client?.name} />
        </div>
      </div>
    </div>
  );
};

export default ClientDealPrintView;
